import React, { useCallback, useEffect, useMemo, useState, lazy } from 'react';
import { PlayIcon, ChevronLeftIcon, ChevronRightIcon, XIcon } from 'lucide-react';
import { useTranslation } from '../lib/i18n';
type Category = 'standard' | 'raptor' | 'sca' | 'action' | 'detail';
type MediaItem = {
  id: number;
  type: 'video';
  url: string;
  thumbnail?: string;
  alt: string;
  categories: Category[];
} | {
  id: number;
  type: 'image';
  url: string;
  alt: string;
  categories: Category[];
};
const mediaItems: MediaItem[] = [
// ───── Row 1: Hero showcase ─────
{
  id: 101,
  type: 'video',
  url: 'https://cdn.magicpatterns.com/uploads/6JoZpMvh4xhLYyr821YybA/Captions_8EB7DF.mov',
  alt: 'D-TECH električni transporter u radu - video demonstracija 1',
  categories: ['standard', 'action']
}, {
  id: 102,
  type: 'video',
  url: 'https://cdn.magicpatterns.com/uploads/7dYWSt8SFfDZKCqzW8rBPA/Captions_343568.mp4',
  alt: 'D-TECH električni transporter u radu - video demonstracija 2',
  categories: ['standard', 'action']
}, {
  id: 103,
  type: 'video',
  url: 'https://cdn.magicpatterns.com/uploads/wE9CK3kwb5qPv5aA5R58AR/Captions_AEADA8.mov',
  alt: 'D-TECH električni transporter u radu - video demonstracija 3',
  categories: ['standard', 'action']
}, {
  id: 100,
  type: 'video',
  url: 'https://cdn.magicpatterns.com/uploads/vLcLGgk9BGschXmPHpTw34/Captions_3D1F12.mp4',
  alt: 'D-TECH električni transporteri za stepenice - glavna video demonstracija',
  categories: ['standard', 'action']
}, {
  id: 1,
  type: 'video',
  url: 'https://cdn.magicpatterns.com/uploads/mSYYNkJyukoDMZMh7e8PPt/SCA.mp4',
  thumbnail: 'https://cdn.magicpatterns.com/uploads/akDJq68nVZQ2xPt6hvjWBg/1.jpg',
  alt: 'D-TECH SCA električni transporter za stepenice u radu - video demonstracija',
  categories: ['sca', 'action']
}, {
  id: 2,
  type: 'image',
  url: 'https://cdn.magicpatterns.com/uploads/ghMXzz1CMYgSVXqQa25mVr/SCB_300_RAPTOR_2.jpg',
  alt: 'D-TECH RAPTOR heavy-duty električni guseničar za teške terete',
  categories: ['raptor']
}, {
  id: 3,
  type: 'image',
  url: 'https://cdn.magicpatterns.com/uploads/6UrQgTMFhTx7ASAvR9T6xo/DR3.jpg',
  alt: 'D-TECH SCB električni guseničar za stepenice na neravnom građevinskom terenu i kamenu',
  categories: ['standard', 'action']
},
// ───── Row 2: Load-carrying proof (frižider, Knauf, dimenzije) ─────
{
  id: 4,
  type: 'image',
  url: 'https://cdn.magicpatterns.com/uploads/gmRwVrHMKeHMHcPMRrQpQi/dtech1jpg.jpg',
  alt: 'D-TECH mašina za prevoz tereta uz stepenice - nosi građevinski materijal Knauf insulation u zgradi',
  categories: ['standard', 'action']
}, {
  id: 5,
  type: 'image',
  url: 'https://cdn.magicpatterns.com/uploads/pLgroDA9ZSThfg1TYNR6HZ/dtech2jpg.jpg',
  alt: 'Električna kolica za stepenice D-TECH - bezbedan transport frižidera i bele tehnike uz stepenice',
  categories: ['standard', 'action']
}, {
  id: 6,
  type: 'image',
  url: 'https://cdn.magicpatterns.com/uploads/ngiMuA3L6xSM5uzicGnr3B/Dimenzije.jpg',
  alt: 'Tehnički crtež i dimenzije D-TECH SCB transportera za stepenice - 65×56×120/180 cm',
  categories: ['standard', 'detail']
},
// ───── Row 3: Product line + action references ─────
{
  id: 7,
  type: 'image',
  url: 'https://cdn.magicpatterns.com/uploads/7fBFdHuCpVLPhJs3osuFzQ/dtech_4x.jpg',
  alt: 'D-TECH SCB STANDARD - četiri električna transportera za stepenice',
  categories: ['standard']
}, {
  id: 8,
  type: 'image',
  url: 'https://cdn.magicpatterns.com/uploads/o9MVyEqLniqqvBnkbHgEUk/IMG_1628.jpg',
  alt: 'Upotreba D-TECH transportera pod opterećenjem na gradilištu u Beogradu sa Rockwool izolacijom',
  categories: ['standard', 'action']
},
// ───── Row 4: More action / use cases ─────
{
  id: 10,
  type: 'image',
  url: 'https://cdn.magicpatterns.com/uploads/qyfkmJ5HdCXFNMAE1BiTXA/DL.jpg',
  alt: 'D-TECH SCB STANDARD električni transporter za stepenice u magacinu pored industrijskih stepenica',
  categories: ['standard', 'action']
}, {
  id: 11,
  type: 'image',
  url: 'https://cdn.magicpatterns.com/uploads/gCeTbH8xLWHVY9mCLj1eLx/TT.jpg',
  alt: 'D-TECH SCB električni transporter za prevoz auto guma i pneumatika u vulkanizerskoj radnji',
  categories: ['standard', 'action']
}, {
  id: 12,
  type: 'image',
  url: 'https://cdn.magicpatterns.com/uploads/8r8cdtTjV9BmfGyZPnGsXX/DC1.jpg',
  alt: 'D-TECH SCB STANDARD električni transporter za stepenice na gradilištu pored kamiona',
  categories: ['standard', 'action']
},
// ───── Row 5: SCB STANDARD studio + SCA model ─────
{
  id: 13,
  type: 'image',
  url: 'https://cdn.magicpatterns.com/uploads/fkKJQANuSRMS28bHwmBR3m/DC8.jpg',
  alt: 'Bočni pogled D-TECH SCB sa robusnim gusenicama i dtech.rs brendiranjem',
  categories: ['standard']
}, {
  id: 14,
  type: 'image',
  url: 'https://cdn.magicpatterns.com/uploads/akDJq68nVZQ2xPt6hvjWBg/1.jpg',
  alt: 'D-TECH SCA STANDARD električni penjač za stepenice sa pogonskim točkovima',
  categories: ['sca']
}, {
  id: 15,
  type: 'image',
  url: 'https://cdn.magicpatterns.com/uploads/fiFTm5CkJjaa2vxi6jT12K/2.jpg',
  alt: 'D-TECH SCA transporter za bezbedan prevoz tereta uz stepenice',
  categories: ['sca']
},
// ───── Row 6: SCA + RAPTOR collection ─────
{
  id: 16,
  type: 'image',
  url: 'https://cdn.magicpatterns.com/uploads/cjwunoexxNR1kMK1aoVNDE/3.jpg',
  alt: 'D-TECH SCA električna kolica za stepenice u industrijskom okruženju',
  categories: ['sca']
}, {
  id: 17,
  type: 'image',
  url: 'https://cdn.magicpatterns.com/uploads/gD2ZjiTsT9nHLwkhuMiuBi/raptor1.jpg',
  alt: 'D-TECH RAPTOR heavy-duty električni guseničar za teške terete sa robusnim šasijom',
  categories: ['raptor']
}, {
  id: 18,
  type: 'image',
  url: 'https://cdn.magicpatterns.com/uploads/m5gdkWxPSe4o2kt5XMhxyz/raptor2.jpg',
  alt: 'D-TECH RAPTOR električni guseničar za stepenice - bočni pogled sa anti-slip teksturom',
  categories: ['raptor']
},
// ───── Row 7: RAPTOR + technical details start ─────
{
  id: 19,
  type: 'image',
  url: 'https://cdn.magicpatterns.com/uploads/q1Qf1DEGWD8FZB8q9cy7jg/raptor3.jpg',
  alt: 'D-TECH RAPTOR heavy-duty transporter - prednji pogled sa zaštitnom anti-slip površinom',
  categories: ['raptor']
}, {
  id: 20,
  type: 'image',
  url: 'https://cdn.magicpatterns.com/uploads/i4fc5hm7PXGHKooNAewi3v/DC9.jpg',
  alt: 'Kontrolna tabla D-TECH SCB sa SPEED regulatorom brzine, POWER displejom i UP/DOWN prekidačem',
  categories: ['detail']
}, {
  id: 21,
  type: 'image',
  url: 'https://cdn.magicpatterns.com/uploads/qTdHGWDXwmefbtb1twU3kb/DC3.jpg',
  alt: 'Kontrolna tabla i bezbednosni prekidač na ručki D-TECH električnih kolica za stepenice',
  categories: ['detail']
},
// ───── Row 8+: Pure technical details ─────
{
  id: 22,
  type: 'image',
  url: 'https://cdn.magicpatterns.com/uploads/cMspoKNA4eLDZK8evG6ZPH/DC7.jpg',
  alt: 'Inox ergonomska ručka i upravljač D-TECH SCB električnog transportera za stepenice',
  categories: ['detail']
}, {
  id: 23,
  type: 'image',
  url: 'https://cdn.magicpatterns.com/uploads/vR8Rd7HemtwaVix4FypiDy/DC2.jpg',
  alt: 'Kvalitetne gumene gusenice i zupčasti pogonski mehanizam D-TECH mašine - detalj',
  categories: ['detail']
}, {
  id: 24,
  type: 'image',
  url: 'https://cdn.magicpatterns.com/uploads/gXc7yrp8CXuHuqVnYHvZRm/DC11.jpg',
  alt: 'Kvalitetne gumene gusenice, lančani prenos i pogonski mehanizam D-TECH mašine - detalj',
  categories: ['detail']
}, {
  id: 25,
  type: 'image',
  url: 'https://cdn.magicpatterns.com/uploads/b9e2njb56bsS45oyePBaXu/DC4.jpg',
  alt: 'Gumene gusenice i baterija na D-TECH SCB električnom transporteru za stepenice',
  categories: ['detail']
}, {
  id: 26,
  type: 'image',
  url: 'https://cdn.magicpatterns.com/uploads/9Ydy8kBH6PVEPPdvAy7MB7/DC5.jpg',
  alt: 'Pogonski mehanizam, elektromotor i točkovi D-TECH SCB - detalj sklopa odozdo',
  categories: ['detail']
}, {
  id: 27,
  type: 'image',
  url: 'https://cdn.magicpatterns.com/uploads/j3XBzxtVMPV9PSNYbmvbth/DC10.jpg',
  alt: 'Tovarna platforma D-TECH SCB - široka radna površina za bezbedan prevoz tereta uz stepenice',
  categories: ['detail']
}, {
  id: 28,
  type: 'image',
  url: 'https://cdn.magicpatterns.com/uploads/3Afv5z8GtvpcQyLHa2To5C/IMG_1676.jpg',
  alt: 'D-TECH guseničar sa dtech.rs brendiranjem na neravnom kamenom terenu - detalj gusenica',
  categories: ['detail']
}, {
  id: 29,
  type: 'image',
  url: 'https://cdn.magicpatterns.com/uploads/e47UzGfxnhj52YYPUUCA3A/DC6.jpg',
  alt: 'CE sertifikat i sigurnosna upozorenja na D-TECH SCB električnom transporteru za stepenice',
  categories: ['detail']
}, {
  id: 30,
  type: 'image',
  url: 'https://cdn.magicpatterns.com/uploads/buWaMTtZSYYWjymvQQCNW8/IMG_3318.jpg',
  alt: 'D-TECH SCB električni transporter nosi paletu flaširane vode uz stepenice u zgradi',
  categories: ['standard', 'action']
}, {
  id: 31,
  type: 'image',
  url: 'https://cdn.magicpatterns.com/uploads/sM25FMrfj4cL5W2QcqSq1w/scbb.jpg',
  alt: 'D-TECH SCB električni transporter sa dtech.rs brendiranjem na gradilištu pored kamiona',
  categories: ['standard']
}, {
  id: 32,
  type: 'image',
  url: 'https://cdn.magicpatterns.com/uploads/1fDPoVvpbGbNde3UR7eY6D/cchh.png',
  alt: 'D-TECH RAPTOR električni transporter - detalj',
  categories: ['raptor']
}, {
  id: 33,
  type: 'image',
  url: 'https://cdn.magicpatterns.com/uploads/1Y3AXEhuhxSH8VSoLoWcG2/cchh1.png',
  alt: 'D-TECH SCB električni transporter - detalj',
  categories: ['standard']
}, {
  id: 34,
  type: 'image',
  url: 'https://cdn.magicpatterns.com/uploads/o94LKM7nMkgeh4VFkzFw93/IMG_9857.jpg',
  alt: 'D-TECH električni transporter transportuje gume uz stepenice',
  categories: ['standard', 'action']
}, {
  id: 35,
  type: 'image',
  url: 'https://cdn.magicpatterns.com/uploads/3i5sc7jLn5bVrftDumbSn2/IMG_9904.jpg',
  alt: 'D-TECH električni transporter transportuje frižider',
  categories: ['standard', 'action']
}];
type FilterKey = 'all' | Category;
const FILTERS: {
  key: FilterKey;
  sr: string;
  en: string;
  ru: string;
}[] = [{
  key: 'all',
  sr: 'Sve',
  en: 'All',
  ru: 'Все'
}, {
  key: 'standard',
  sr: 'SCB STANDARD',
  en: 'SCB STANDARD',
  ru: 'SCB STANDARD'
}, {
  key: 'raptor',
  sr: 'RAPTOR',
  en: 'RAPTOR',
  ru: 'RAPTOR'
}, {
  key: 'sca',
  sr: 'SCA',
  en: 'SCA',
  ru: 'SCA'
}, {
  key: 'action',
  sr: 'U akciji',
  en: 'In action',
  ru: 'В работе'
}, {
  key: 'detail',
  sr: 'Detalji',
  en: 'Details',
  ru: 'Детали'
}];
export function GalleryPage() {
  const {
    lang
  } = useTranslation();
  const en = lang === 'en';
  const ru = lang === 'ru';
  const [filter, setFilter] = useState<FilterKey>('all');
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const visibleItems = useMemo(() => {
    if (filter === 'all') return mediaItems;
    return mediaItems.filter(m => m.categories.includes(filter));
  }, [filter]);
  const counts = useMemo(() => {
    const result: Record<FilterKey, number> = {
      all: mediaItems.length,
      standard: 0,
      raptor: 0,
      sca: 0,
      action: 0,
      detail: 0
    };
    mediaItems.forEach(m => {
      m.categories.forEach(c => {
        result[c] += 1;
      });
    });
    return result;
  }, []);
  const handleClose = () => setActiveIndex(null);
  const handlePrev = useCallback(() => {
    if (activeIndex === null) return;
    setActiveIndex(prev => prev! === 0 ? visibleItems.length - 1 : prev! - 1);
  }, [activeIndex, visibleItems.length]);
  const handleNext = useCallback(() => {
    if (activeIndex === null) return;
    setActiveIndex(prev => prev! === visibleItems.length - 1 ? 0 : prev! + 1);
  }, [activeIndex, visibleItems.length]);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (activeIndex === null) return;
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeIndex, handlePrev, handleNext]);
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStart === null) return;
    const touchEnd = e.changedTouches[0].clientX;
    const diff = touchStart - touchEnd;
    if (diff > 50) {
      handleNext();
    } else if (diff < -50) {
      handlePrev();
    }
    setTouchStart(null);
  };
  const activeMedia = activeIndex !== null ? visibleItems[activeIndex] : null;
  return <main className="min-h-screen bg-dark-bg" data-id="element-776" data-mp="bvspjjt51lwv">
      <section className="py-20 bg-dark-surface border-b-2 border-dark-border" data-id="element-777" data-mp="gw6swojso7ok">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-778" data-mp="1z7mh40oikvo">
          <h1 className="text-5xl md:text-6xl font-black text-white mb-6" data-id="element-779" data-mp="2223elkhhnkc">
            <span className="text-brand-yellow" data-id="element-780" data-mp="703pzgh4c50k">
              {en ? 'Gallery' : ru ? 'Галерея' : 'Galerija'}
            </span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto" data-id="element-781" data-mp="3u302tyo1dj3d">
            {en ? 'See D-TECH machines in action and explore their robust design.' : ru ? 'Посмотрите машины D-TECH в действии и изучите их прочный дизайн.' : 'Pogledajte D-TECH mašine u akciji i istražite njihov robustan dizajn.'}
          </p>
        </div>
      </section>

      <section className="py-20" data-id="element-782" data-mp="3jfowe36h3bpq">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-783" data-mp="3jhi13cxizlv3">
          {/* Filter chips */}
          <div className="mb-10 flex flex-wrap gap-2 sm:gap-3 justify-center" data-id="element-784" data-mp="3efsohsunn04w">
            {FILTERS.map(f => {
            const active = filter === f.key;
            const label = en ? f.en : ru ? f.ru : f.sr;
            return <button key={f.key} onClick={() => setFilter(f.key)} className={`px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wide border-2 transition-all ${active ? 'bg-brand-yellow text-dark-bg border-brand-yellow shadow-lg shadow-brand-yellow/20' : 'bg-dark-surface text-gray-300 border-dark-border hover:border-brand-yellow hover:text-white'}`} data-id="element-785" data-mp="30ppb7rdr6grl">
                  {label}
                  <span className={`ml-2 text-xs ${active ? 'text-dark-bg/70' : 'text-gray-500'}`} data-id="element-786" data-mp="2hrczbxvgissb">
                    {counts[f.key]}
                  </span>
                </button>;
          })}
          </div>

          <div key={filter} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 animate-fade-in" data-id="element-787" data-mp="1698t9q1r00p9">
            {visibleItems.map((media, index) => <button key={media.id} onClick={() => setActiveIndex(index)} className="relative aspect-[4/5] bg-dark-surface border-2 border-dark-border rounded-industrial overflow-hidden hover:border-brand-yellow transition-colors group" data-id="element-788" data-mp="ej3z4zo6rzau" data-mpi={index}>
                {media.type === 'video' && !media.thumbnail ? <video src={`${media.url}#t=0.001`} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" muted playsInline preload="metadata" data-id="element-789" data-mp="3qzpquday9pqt" /> : <img src={media.type === 'video' ? media.thumbnail : media.url} alt={media.alt} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" loading="lazy" data-id="element-790" data-mp="y60xuofyrelz" />}
                {media.type === 'video' && <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/10 transition-colors" data-id="element-791" data-mp="f6d822aid2hv">
                    <div className="w-16 h-16 bg-brand-yellow rounded-full flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform" data-id="element-792" data-mp="t77hmo9al9i4">
                      <PlayIcon className="w-8 h-8 text-dark-bg ml-1" data-id="element-793" data-mp="24l1msa6kmwj2" />
                    </div>
                  </div>}
              </button>)}
          </div>

          {visibleItems.length === 0 && <p className="text-center text-gray-400 py-12" data-id="element-794" data-mp="282fugxt8iftt">
              {en ? 'No items in this category.' : ru ? 'Нет элементов в этой категории.' : 'Nema stavki u ovoj kategoriji.'}
            </p>}
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-6 bg-dark-bg border-t border-dark-border" data-id="element-795" data-mp="2368oj50u94bv">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-796" data-mp="2qu8nutc7u322">
          <p className="text-xs text-gray-500 max-w-3xl mx-auto" data-id="element-797" data-mp="2vrrzrmlfy3qu">
            {en ? 'Product photos on the website are for informational purposes and may differ from the actual appearance of the product due to technical modifications by the supplier.' : ru ? 'Фотографии продуктов на сайте носят информационный характер и могут отличаться от реального внешнего вида продукта из-за технических модификаций поставщика.' : 'Fotografije proizvoda na sajtu su informativnog karaktera i mogu odstupati od stvarnog izgleda proizvoda usled tehničkih modifikacija dobavljača.'}
          </p>
        </div>
      </section>

      {/* Fullscreen Gallery Modal */}
      {activeIndex !== null && activeMedia && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm" data-id="element-798" data-mp="10pn8zwqsta1t">
          <button onClick={handleClose} className="absolute top-6 right-6 p-2 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-colors z-50" data-id="element-799" data-mp="1xfntvko1r6du">
            <XIcon className="w-8 h-8" data-id="element-800" data-mp="1oepwvmc0zz4r" />
          </button>

          <button onClick={handlePrev} className="absolute left-4 md:left-8 p-3 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-colors z-50 hidden md:block" data-id="element-801" data-mp="1ohc4tn2twgni">
            <ChevronLeftIcon className="w-8 h-8" data-id="element-802" data-mp="819bdi3mgfqs" />
          </button>

          <div className="w-full h-full flex items-center justify-center p-4 md:p-12" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd} data-id="element-803" data-mp="83c8sn73tb5j">
            {activeMedia.type === 'image' ? <img src={activeMedia.url} alt={activeMedia.alt} className="max-w-full max-h-full object-contain select-none" draggable={false} data-id="element-804" data-mp="2sorczmlksdix" /> : <video src={activeMedia.url} controls autoPlay className="max-w-full max-h-full outline-none" data-id="element-805" data-mp="32ekqwu96x4qc" />}
          </div>

          <button onClick={handleNext} className="absolute right-4 md:right-8 p-3 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-colors z-50 hidden md:block" data-id="element-806" data-mp="3cb5vxfl7mfu6">
            <ChevronRightIcon className="w-8 h-8" data-id="element-807" data-mp="1m019u4siquq4" />
          </button>

          {/* Mobile swipe indicator */}
          <div className="absolute bottom-8 left-0 right-0 flex justify-center gap-2 z-50" data-id="element-808" data-mp="15uvsrjbyllfg">
            {visibleItems.map((_, idx) => <div key={idx} className={`w-2 h-2 rounded-full transition-colors ${idx === activeIndex ? 'bg-brand-yellow' : 'bg-white/30'}`} data-id="element-809" data-mp="1aqundonvrzob" data-mpi={idx} />)}
          </div>
        </div>}
    </main>;
}